import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { useT } from "../lang";

const T = {
  pt: {
    label: "03 · experiência",
    currentYears: "2023 – até agora",
    currentPlace: "Independente, Brooklyn (NY)",
    currentRole: "Consultoria Criativa · Gestão de Comunidade · Produção",
    rangeLabel: "agências & redações · são paulo · brasil",
    rangeYears: "2015 – 2023",
    formation: "formação",
    languages: "idiomas",
    ptrole: "fluente · nativa",
    enrole: "fluente",
    skillsLabel: "skills",
    skillsBody:
      "Estratégia de conteúdo · Community management · Copywriting · Storytelling criativo · Análise de dados · Monitoramento de tendências · Gestão de influenciadores · Social media",
    roles: {
      "Núcleo Jornalismo, SP": "Editora de Redes Sociais · Conteúdo · Gestão de Comunidade",
      "Artplan, SP": "Estrategista de Conteúdo · Redação",
      "CNN Brasil, SP": "Especialista em Redes Sociais",
      "Oliver Agency · iHouse Itaú, SP": "Gestora de Comunidade",
      "Mutato, SP": "Estrategista de Conteúdo · Redação",
      "BuzzFeed Brasil, SP": "Estrategista de Redes Sociais · Gestão de Comunidade",
      "Mullen Lowe Brasil, SP": "Gestão de Comunidade",
    },
    courses: {
      "2025": "Inglês para Negócios",
      "2024": "Escrita Criativa",
      "2023-suny": "Ensino de Inglês como Segunda Língua",
      "2018": "Bacharelado em Jornalismo · Foco em Redação",
    },
    cities: {
      "New York, NY": "New York, NY",
      "São Paulo, Brasil": "São Paulo, Brasil",
    },
  },
  en: {
    label: "03 · experience",
    currentYears: "2023 – present",
    currentPlace: "Independent, Brooklyn (NY)",
    currentRole: "Creative Consulting · Community Management · Production",
    rangeLabel: "agencies & newsrooms · são paulo · brazil",
    rangeYears: "2015 – 2023",
    formation: "education",
    languages: "languages",
    ptrole: "fluent · native",
    enrole: "fluent",
    skillsLabel: "skills",
    skillsBody:
      "Content strategy · Community management · Copywriting · Creative storytelling · Data analysis · Trend monitoring · Influencer management · Social media",
    roles: {
      "Núcleo Jornalismo, SP": "Social Media Editor · Content · Community Management",
      "Artplan, SP": "Content Strategist · Copywriter",
      "CNN Brasil, SP": "Social Media Specialist",
      "Oliver Agency · iHouse Itaú, SP": "Community Manager",
      "Mutato, SP": "Content Strategist · Copywriter",
      "BuzzFeed Brasil, SP": "Social Media Strategist · Community Management",
      "Mullen Lowe Brasil, SP": "Community Management",
    },
    courses: {
      "2025": "Business English",
      "2024": "Creative Writing",
      "2023-suny": "Teaching English as a Second Language",
      "2018": "Journalism Bachelor's · Copywriting Focus",
    },
    cities: {
      "New York, NY": "New York, NY",
      "São Paulo, Brasil": "São Paulo, Brazil",
    },
  },
};

const timeline = [
  { place: "Núcleo Jornalismo, SP" },
  { place: "Artplan, SP" },
  { place: "CNN Brasil, SP" },
  { place: "Oliver Agency · iHouse Itaú, SP" },
  { place: "Mutato, SP" },
  { place: "BuzzFeed Brasil, SP" },
  { place: "Mullen Lowe Brasil, SP" },
];

const brands = [
  "Johnson & Johnson",
  "Johnson's Baby",
  "Sundown",
  "Neutrogena",
  "Aveeno",
  "Sempre Livre",
  "Carefree",
  "Listerine",
  "Hipoglós",
  "Tylenol",
  "Nicorette",
  "Cacau Show",
  "Dotz",
  "La Roche-Posay",
  "3 Corações",
  "Tintas Coral",
  "Targifor C",
  "Mimmo",
  "Skol",
  "Trident",
  "Mercedes-Benz",
  "Shell",
  "BuzzFeed",
  "Itaú",
  "Can Viagem e Gastronomia",
  "Toddy",
  "Ipiranga",
  "Jack Daniel's",
  "Kopenhagen",
  "Addera",
  "Buscofem",
  "Buscopan",
  "Benegrip",
  "BluMel",
  "Colflex",
  "Engov",
  "Neosaldina",
  "Neo Química",
  "Vitasay",
  "Zero-Cal",
  "Núcleo Jornalismo",
];

const formation = [
  { years: "2025", place: "Columbia University", city: "New York, NY", courseKey: "2025" },
  { years: "2024", place: "Yellow Brick", city: "New York, NY", courseKey: "2024" },
  { years: "2023", place: "SUNY Westchester", city: "New York, NY", courseKey: "2023-suny" },
  { years: "2018", place: "Universidade São Judas Tadeu", city: "São Paulo, Brasil", courseKey: "2018" },
];

export default function Skills() {
  const t = useT(T);
  return (
    <section
      id="skills"
      data-testid="skills-section"
      className="relative py-24 md:py-32"
    >
      <div className="container-wide mb-16">
        <div className="flex items-center gap-3 mb-10">
          <span className="label">{t.label}</span>
          <span className="text-2xl">☻</span>
        </div>
      </div>

      {/* Marquee */}
      <div className="tape mb-16" data-testid="agency-marquee">
        <Marquee gradient={false} speed={50}>
          {brands.map((a, i) => (
            <span
              key={i}
              className="mx-6 font-display uppercase text-lg md:text-xl"
            >
              {a}
              <span className="mx-4 text-off/60">☺</span>
            </span>
          ))}
        </Marquee>
      </div>

      <div className="container-wide">
        <div className="border-t border-[color:var(--ink)]">
          {/* Current independent entry */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-12 gap-4 items-baseline py-5 border-b border-[color:var(--ink)]"
            data-testid="timeline-current"
          >
            <span className="col-span-12 md:col-span-3 font-narrow text-xs md:text-sm">
              {t.currentYears}
            </span>
            <span className="col-span-12 md:col-span-5 font-display uppercase text-base md:text-lg">
              {t.currentPlace}
            </span>
            <span className="col-span-12 md:col-span-4 md:text-right font-narrow text-xs md:text-sm text-brick">
              {t.currentRole}
            </span>
          </motion.div>

          {/* Grouped past agencies */}
          <div
            className="grid grid-cols-12 gap-4 items-baseline pt-8 pb-3 border-b border-[color:var(--ink)]/30"
            data-testid="timeline-range-label"
          >
            <span className="col-span-12 md:col-span-3 font-narrow text-xs md:text-sm">
              {t.rangeYears}
            </span>
            <span className="col-span-12 md:col-span-9 label">
              {t.rangeLabel}
            </span>
          </div>

          {timeline.map((tl, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="grid grid-cols-12 gap-4 items-baseline py-4 border-b border-[color:var(--ink)]"
              data-testid={`timeline-${i}`}
            >
              <span className="col-span-12 md:col-span-3 font-narrow text-xs md:text-sm text-[color:var(--ink)]/50">
                · · ·
              </span>
              <span className="col-span-12 md:col-span-5 font-display uppercase text-base md:text-lg">
                {tl.place}
              </span>
              <span className="col-span-12 md:col-span-4 md:text-right font-narrow text-xs md:text-sm text-brick">
                {t.roles[tl.place] || tl.place}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Formation */}
        <div className="mt-14">
          <div className="label mb-6">{t.formation}</div>
          <div className="border-t border-[color:var(--ink)]">
            {formation.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="grid grid-cols-12 gap-4 items-baseline py-4 border-b border-[color:var(--ink)]"
                data-testid={`formation-${i}`}
              >
                <span className="col-span-12 md:col-span-3 font-narrow text-xs md:text-sm">
                  {f.years}
                </span>
                <span className="col-span-12 md:col-span-5 font-display uppercase text-base md:text-lg">
                  {f.place}
                </span>
                <span className="col-span-12 md:col-span-4 md:text-right font-narrow text-xs md:text-sm text-brick">
                  {t.courses[f.courseKey] ? (
                    <>
                      {t.courses[f.courseKey]}
                      <span className="block text-[color:var(--ink)]/60">
                        {t.cities[f.city] || f.city}
                      </span>
                    </>
                  ) : (
                    t.cities[f.city] || f.city
                  )}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Languages + Skills */}
        <div className="mt-10 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-5 panel">
            <div className="label !text-off mb-4">{t.languages}</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-off/30 p-4">
                <div className="font-display uppercase text-3xl md:text-4xl leading-none">
                  PT-BR
                </div>
                <div className="mt-2 font-narrow text-xs uppercase tracking-widest text-off/85">
                  {t.ptrole}
                </div>
              </div>
              <div className="border border-off/30 p-4">
                <div className="font-display uppercase text-3xl md:text-4xl leading-none">
                  EN
                </div>
                <div className="mt-2 font-narrow text-xs uppercase tracking-widest text-off/85">
                  {t.enrole}
                </div>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-7 border border-[color:var(--ink)] p-8 md:p-10">
            <div className="label mb-3">{t.skillsLabel}</div>
            <div className="font-narrow text-sm md:text-base leading-relaxed">
              {t.skillsBody}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
